'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Highlight {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export default function WhyChooseSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const highlights: Highlight[] = [
    {
      id: 'highlight_practical',
      title: 'Practical Training',
      description: 'Learn by doing with hands-on projects and real-world scenarios',
      icon: 'WrenchScrewdriverIcon',
    },
    {
      id: 'highlight_industry',
      title: 'Industry-Oriented',
      description: 'Curriculum designed based on current industry requirements',
      icon: 'BriefcaseIcon',
    },
    {
      id: 'highlight_trainers',
      title: 'Expert Trainers',
      description: 'Learn from experienced professionals with proven expertise',
      icon: 'UserGroupIcon',
    },
    {
      id: 'highlight_affordable',
      title: 'Affordable Fees',
      description: 'Quality education at competitive prices with flexible payment',
      icon: 'CurrencyRupeeIcon',
    },
    {
      id: 'highlight_jobready',
      title: 'Job-Oriented Skills',
      description: 'Focus on skills that employers are actively seeking',
      icon: 'RocketLaunchIcon',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <span className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Icon name="SparklesIcon" size={16} className="text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Why Choose Us
            </span>
          </span>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none text-foreground mb-4">
            What Makes Us<br />
            <span className="text-primary">Different</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We focus on practical, industry-ready training that prepares you for real careers
          </p>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {highlights.map((highlight, index) => (
            <div
              key={highlight.id}
              className={`group relative bg-card border border-border rounded-2xl p-8 hover:border-primary/50 hover:shadow-xl transition-all duration-300 ${
                isVisible ? 'animate-fadeIn' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-primary/10 mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Icon name={highlight.icon as any} size={32} className="text-primary group-hover:text-primary-foreground transition-colors" />
              </div>

              {/* Content */}
              <h3 className="font-display text-2xl uppercase tracking-tight text-foreground mb-3">
                {highlight.title}
              </h3>
              <p className="text-base text-muted-foreground leading-relaxed">
                {highlight.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}